/**
 * The run report is delivered as an opaque `JSON` scalar, so everything here
 * narrows it defensively: a report written by an older worker, or one truncated
 * by a failure partway through, still renders instead of crashing the page.
 */

/** Per-billing-party counts as the report records them. */
export type PieceBackfillBillingPartySummary = {
  readonly contactUuid: string;
  readonly contactName: string;
  readonly packagesScanned: number;
  readonly packagesSkipped: number;
  readonly piecesToCreate: number;
  readonly statusUpdates: number;
  readonly locationUpdates: number;
};

/** What the run planned across every billing party. */
export type PieceBackfillReportTotals = {
  readonly packagesScanned: number;
  readonly packagesSkipped: number;
  readonly piecesToCreate: number;
  readonly statusUpdates: number;
  readonly locationUpdates: number;
};

/** What the run actually wrote. All zero for a dry run. */
export type PieceBackfillAppliedCounts = {
  readonly piecesCreated: number;
  readonly joinsCreated: number;
  readonly piecesStatusUpdated: number;
  readonly piecesLocationUpdated: number;
};

/** One row of the post-run piece status histogram. */
export type PieceBackfillStatusCount = {
  readonly status: string;
  readonly count: number;
};

/**
 * End-of-run consistency checks. A `null` field means that check could not be
 * run; the reason is in `warnings`.
 */
export type PieceBackfillVerification = {
  readonly totalPieces: number | null;
  readonly totalPackages: number | null;
  readonly packagesWithoutPieces: number | null;
  readonly statusDistribution: readonly PieceBackfillStatusCount[] | null;
  readonly piecesBehindOrderStatus: number | null;
  readonly warnings: readonly string[];
};

/** A run report, narrowed for rendering. */
export type PieceBackfillReportView = {
  readonly mode: string | null;
  readonly cutoffDate: string | null;
  readonly billingParties: readonly PieceBackfillBillingPartySummary[];
  readonly totals: PieceBackfillReportTotals;
  readonly applied: PieceBackfillAppliedCounts;
  readonly warnings: readonly string[];
  readonly notes: readonly string[];
  readonly verification: PieceBackfillVerification | null;
};

/**
 * Whether a value is a plain keyed object, which is the only shape worth
 * reading fields off.
 *
 * @param value The value to test.
 * @returns True for non-null, non-array objects.
 */
const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

/**
 * Reads a finite number field, defaulting anything else to zero.
 *
 * @param source The containing record.
 * @param key The field name.
 * @returns The number, or 0 when absent or not a finite number.
 */
const readCount = (source: Record<string, unknown>, key: string): number => {
  const value = source[key];
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
};

/**
 * Reads a number field that is meaningfully absent, keeping the distinction
 * between "zero" and "this check did not run".
 *
 * @param source The containing record.
 * @param key The field name.
 * @returns The number, or `null` when absent or not a finite number.
 */
const readNullableCount = (
  source: Record<string, unknown>,
  key: string,
): number | null => {
  const value = source[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
};

/**
 * Reads a string field.
 *
 * @param source The containing record.
 * @param key The field name.
 * @returns The string, or `null` when absent or not a string.
 */
const readString = (
  source: Record<string, unknown>,
  key: string,
): string | null => {
  const value = source[key];
  return typeof value === "string" ? value : null;
};

/**
 * Reads a list of strings, dropping non-string entries.
 *
 * @param source The containing record.
 * @param key The field name.
 * @returns The strings, or an empty array when the field is absent or not an
 *   array.
 */
const readStringArray = (
  source: Record<string, unknown>,
  key: string,
): string[] => {
  const value = source[key];
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((entry): entry is string => typeof entry === "string");
};

/**
 * Reads a list of records, dropping entries that are not objects.
 *
 * @param source The containing record.
 * @param key The field name.
 * @returns The records, or an empty array when the field is absent or not an
 *   array.
 */
const readRecordArray = (
  source: Record<string, unknown>,
  key: string,
): Array<Record<string, unknown>> => {
  const value = source[key];
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((entry) => isRecord(entry));
};

/**
 * Narrows the `totals` section, treating a missing section as all zeroes.
 *
 * @param value The raw section.
 * @returns The narrowed totals.
 */
const parseTotals = (value: unknown): PieceBackfillReportTotals => {
  const source = isRecord(value) ? value : {};
  return {
    packagesScanned: readCount(source, "packagesScanned"),
    packagesSkipped: readCount(source, "packagesSkipped"),
    piecesToCreate: readCount(source, "piecesToCreate"),
    statusUpdates: readCount(source, "statusUpdates"),
    locationUpdates: readCount(source, "locationUpdates"),
  };
};

/**
 * Narrows the `applied` section, treating a missing section as all zeroes (which
 * is also what a dry run reports).
 *
 * @param value The raw section.
 * @returns The narrowed applied counts.
 */
const parseApplied = (value: unknown): PieceBackfillAppliedCounts => {
  const source = isRecord(value) ? value : {};
  return {
    piecesCreated: readCount(source, "piecesCreated"),
    joinsCreated: readCount(source, "joinsCreated"),
    piecesStatusUpdated: readCount(source, "piecesStatusUpdated"),
    piecesLocationUpdated: readCount(source, "piecesLocationUpdated"),
  };
};

/**
 * Narrows the piece status histogram.
 *
 * @param value The raw distribution.
 * @returns The rows, or `null` when the check did not run.
 */
const parseStatusDistribution = (
  value: unknown,
): PieceBackfillStatusCount[] | null => {
  if (!Array.isArray(value)) {
    return null;
  }
  return value
    .filter((entry) => isRecord(entry))
    .map((entry) => ({
      status: readString(entry, "status") ?? "unknown",
      count: readCount(entry, "count"),
    }));
};

/**
 * Narrows the verification section.
 *
 * @param value The raw section.
 * @returns The narrowed verification, or `null` when the run recorded none.
 */
const parseVerification = (
  value: unknown,
): PieceBackfillVerification | null => {
  if (!isRecord(value)) {
    return null;
  }
  return {
    totalPieces: readNullableCount(value, "totalPieces"),
    totalPackages: readNullableCount(value, "totalPackages"),
    packagesWithoutPieces: readNullableCount(value, "packagesWithoutPieces"),
    statusDistribution: parseStatusDistribution(value.statusDistribution),
    piecesBehindOrderStatus: readNullableCount(
      value,
      "piecesBehindOrderStatus",
    ),
    warnings: readStringArray(value, "warnings"),
  };
};

/**
 * Narrows a run report for rendering.
 *
 * @param value The `report` JSON scalar from the run view.
 * @returns The narrowed report, or `null` when the run has not written one yet.
 */
export const parsePieceBackfillReport = (
  value: unknown,
): PieceBackfillReportView | null => {
  if (!isRecord(value)) {
    return null;
  }
  return {
    mode: readString(value, "mode"),
    cutoffDate: readString(value, "cutoffDate"),
    billingParties: readRecordArray(value, "billingParties").map((entry) => ({
      contactUuid: readString(entry, "contactUuid") ?? "",
      contactName: readString(entry, "contactName") ?? "Unknown billing party",
      packagesScanned: readCount(entry, "packagesScanned"),
      packagesSkipped: readCount(entry, "packagesSkipped"),
      piecesToCreate: readCount(entry, "piecesToCreate"),
      statusUpdates: readCount(entry, "statusUpdates"),
      locationUpdates: readCount(entry, "locationUpdates"),
    })),
    totals: parseTotals(value.totals),
    applied: parseApplied(value.applied),
    warnings: readStringArray(value, "warnings"),
    notes: readStringArray(value, "notes"),
    verification: parseVerification(value.verification),
  };
};

/**
 * Collects every warning the report carries, including the ones the backend
 * degraded a failed verification check into. Verification warnings are the
 * reason a run can read as COMPLETED while a check silently did not run, so the
 * UI shows this combined list rather than the top-level array alone.
 *
 * @param report The narrowed report.
 * @returns Warnings in report order, verification warnings last.
 */
export const collectReportWarnings = (
  report: PieceBackfillReportView,
): string[] => {
  return [...report.warnings, ...(report.verification?.warnings ?? [])];
};

/**
 * Whether the report's own verification says a re-run would still find work.
 * Packages left with no piece at all, or pieces sitting behind the status their
 * order implies, both mean the backfill is not finished.
 *
 * @param report The narrowed report.
 * @returns True when verification reports residual work, false when it reports
 *   none or could not measure it.
 */
export const hasResidualWork = (report: PieceBackfillReportView): boolean => {
  const { verification } = report;
  if (verification === null) {
    return false;
  }
  return (
    (verification.packagesWithoutPieces ?? 0) > 0 ||
    (verification.piecesBehindOrderStatus ?? 0) > 0
  );
};
